import { useAttachmentViewUrl } from '@commenda/api/commenda-ai/useAttachmentViewUrl';
import { Skeleton } from '@commenda/kakashi';
import { getFileIcon } from '@commenda/utils';
import { EyeIcon, FolderOpenIcon, FolderPlusIcon } from 'lucide-react';
import { AttachmentPreviewHoverCard } from './AttachmentPreviewHoverCard';
import {
  formatAttachmentName,
  getAttachmentIconExt,
  getAttachmentKind,
} from './attachment-kind';

const CARD_ACTION_CLASS =
  'flex shrink-0 cursor-pointer items-center justify-center gap-xs rounded-sm border-[0.5px] border-border-primary bg-bg-primary px-md py-sm txt-small-medium text-text-secondary shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)] transition-colors hover:text-text-primary';

export interface AttachmentCardSaveConfig {
  isSaved: boolean;
  onSave: () => void;
  onManage: () => void;
}

/** Standardized in-message document card (Figma 61:21970). A featured file icon — or the image
 * itself for images — with the filename, format label, a preview button (hover for a peek, click to
 * open the {@link AttachmentPreviewPanel}), Save to Documents for a file the agent generated, and a
 * Download button. */
export function AttachmentCard({
  fileName,
  mimeType,
  fileRef,
  downloadUrl,
  onPreview,
  saveConfig,
}: {
  fileName: string;
  mimeType: string;
  /** Stored attachment reference (raw or signed URL); resolved to a viewable URL for the thumbnail. */
  fileRef: string;
  /** Forced-download URL. */
  downloadUrl?: string;
  onPreview?: () => void;
  saveConfig?: AttachmentCardSaveConfig;
}) {
  const { kind, label } = getAttachmentKind(mimeType, fileName, fileRef);
  const isImage = kind === 'image';
  // Resolve a viewable URL for the thumbnail only when it's an image; a failed read falls back to the icon.
  const { url: thumbUrl, isLoading: isThumbLoading } = useAttachmentViewUrl(
    fileRef,
    isImage
  );

  const iconExt = getAttachmentIconExt(kind, fileName, fileRef);

  const FileTypeIcon = getFileIcon(iconExt ? `file.${iconExt}` : fileName);

  return (
    <div className="flex w-full items-center gap-lg rounded-lg border-[0.5px] border-border-primary bg-bg-primary p-xl shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]">
      {isImage && isThumbLoading ? (
        <Skeleton className="size-10 shrink-0 rounded-sm" />
      ) : isImage && thumbUrl ? (
        <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-bg-secondary">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            alt={fileName}
            src={thumbUrl}
            className="size-full object-cover"
          />
        </span>
      ) : (
        // Size via the wrapper (`[&>svg]:size-full`): some @commenda/icons glyphs don't forward
        // className, so a class on the icon itself is silently ignored — targeting the child svg works.
        <span className="flex size-10 shrink-0 items-center justify-center [&>svg]:size-full">
          <FileTypeIcon />
        </span>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate txt-small-medium text-text-primary">
          {formatAttachmentName(fileName)}
        </span>
        <span className="txt-extra-small-regular text-text-secondary">
          {label}
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-md">
        {onPreview && (
          <AttachmentPreviewHoverCard
            fileName={fileName}
            mimeType={mimeType}
            fileRef={fileRef}
          >
            <button
              type="button"
              aria-label="Preview"
              onClick={onPreview}
              className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-sm border-[0.5px] border-border-primary bg-bg-primary text-fg-tertiary shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)] transition-colors hover:text-text-primary"
            >
              <EyeIcon className="size-4" />
            </button>
          </AttachmentPreviewHoverCard>
        )}
        {saveConfig && (
          <button
            type="button"
            onClick={
              saveConfig.isSaved ? saveConfig.onManage : saveConfig.onSave
            }
            className={CARD_ACTION_CLASS}
          >
            {saveConfig.isSaved ? (
              <FolderOpenIcon className="size-4" />
            ) : (
              <FolderPlusIcon className="size-4" />
            )}
            {saveConfig.isSaved ? 'Manage' : 'Save to Documents'}
          </button>
        )}
        {downloadUrl && (
          <a
            href={downloadUrl}
            download={fileName}
            className={CARD_ACTION_CLASS}
          >
            Download
          </a>
        )}
      </div>
    </div>
  );
}
